import React, {useEffect} from 'react';

import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import dayjs from 'dayjs';
import {useParams} from 'react-router-dom';
import {CourseSessionCertificateTypeEnum} from 'src/api/ProactApiClient';
import {
  useGetSessionCertificatePdfMutation,
  useGetSessionCertificateQuery,
  useGetSessionContentCertificatePdfMutation,
  useGetSessionContentCertificateQuery,
} from 'src/redux/queries/courseSession';
import {Subtitle} from 'src/design/atoms/Typography';
import {Spinner} from 'src/design/atoms/Spinner';
import {InfoAlert} from 'src/design/molecules/InfoAlert';
import {BackBar} from 'src/app/layout/BackBar';
import {PageHeader} from 'src/design/organisms/PageHeader';
import {TrainingIcon} from '../../../../../app/training/coursePills';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useDocumentDownload} from 'src/shared/hooks/useDocumentDownload';
import {useTrainingBreadcrumbs} from 'src/shared/hooks/useTrainingBreadcrumbs';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';
import {formatUtcCalendarDate} from 'src/shared/utils/formatters';

function getErrorStatus(error: unknown): number | undefined {
  return error != null && typeof error === 'object' && 'status' in error
    ? ((error as {status: unknown}).status as number | undefined)
    : undefined;
}

export interface CourseSessionCertificateProps {
  isAdminPath: boolean;
}

export const CourseSessionCertificate: React.FC<
  CourseSessionCertificateProps
> = ({isAdminPath}) => {
  const {definitionId, assignmentId, sessionId, contentId} = useParams();

  const {setAlert} = useAlert();
  const {download} = useDocumentDownload();
  const [getSessionPdf, {isLoading: isSessionPdfLoading}] =
    useGetSessionCertificatePdfMutation();
  const [getContentPdf, {isLoading: isContentPdfLoading}] =
    useGetSessionContentCertificatePdfMutation();

  const pSessionId = sessionId ? parseInt(sessionId) : 0;
  const pContentId = contentId ? parseInt(contentId) : 0;

  const {
    data: sessionCertificate,
    isLoading: isSessionCertLoading,
    error: sessionCertError,
  } = useGetSessionCertificateQuery(pSessionId, {
    skip: !sessionId || !!contentId,
  });

  const {
    data: contentCertificate,
    isLoading: isContentCertLoading,
    error: contentCertError,
  } = useGetSessionContentCertificateQuery(
    {id: pSessionId, contentId: pContentId},
    {skip: !sessionId || !contentId}
  );

  const certificate = contentId ? contentCertificate : sessionCertificate;

  const handleDownloadPdf = () =>
    download(
      timeZone =>
        contentId
          ? getContentPdf({id: pSessionId, contentId: pContentId, timeZone}).unwrap()
          : getSessionPdf({id: pSessionId, timeZone}).unwrap(),
      `certificate-${certificate?.certificateNumber ?? pSessionId}.pdf`
    );
  const isLoading = isSessionCertLoading || isContentCertLoading;
  const certError = sessionCertError ?? contentCertError;
  const certErrorStatus = getErrorStatus(certError);
  const certNotFound = certErrorStatus === 404;

  useEffect(() => {
    if (certError && !certNotFound) {
      setAlert(parseErrorMessage(certError), 'error');
    }
  }, [certError, certNotFound]);

  const {breadcrumbs, fallbackTo} = useTrainingBreadcrumbs({
    mode: isAdminPath ? 'certificate' : 'user-certificate',
    definitionId,
    courseName: certificate?.courseName || 'Course',
    assignmentId,
    sessionId,
    assigneeName: certificate?.awardedToName,
  });

  const awardText =
    certificate?.type === CourseSessionCertificateTypeEnum.Course
      ? `${certificate.courseName} training`
      : `${certificate?.contentTitle} quiz in the ${certificate?.courseName} training`;

  // expiresAt is a UTC-midnight calendar day; local formatting would shift it a day.
  const renewalText = certificate?.expiresAt
    ? `Please be aware this certification expires on ${formatUtcCalendarDate(
        certificate.expiresAt
      )}.`
    : undefined;

  return (
    <Box>
      <BackBar fallbackTo={fallbackTo} breadcrumbs={breadcrumbs} />
      {isLoading ? (
        <Spinner loadingText="Loading...." />
      ) : certNotFound ? (
        <InfoAlert>No certificate is available for this session.</InfoAlert>
      ) : certError || !certificate ? (
        <Alert color="red" title="Error">
          Failed to load certificate. Please try again later.
        </Alert>
      ) : (
        <>
          <PageHeader>
            <PageHeader.Title icon={TrainingIcon}>Certificate</PageHeader.Title>
            <PageHeader.Actions>
              <PageHeader.Action
                variant="export"
                onClick={handleDownloadPdf}
                loading={isSessionPdfLoading || isContentPdfLoading}>
                Download PDF
              </PageHeader.Action>
            </PageHeader.Actions>
          </PageHeader>

          <Box
            mt={48}
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              fontSize: '1.2rem',
            }}>
            <Subtitle text="Certificate of Completion" />
            {certificate.certificateNumber && (
              <p style={{fontSize: '0.9rem', color: 'gray'}}>
                Certificate No. {certificate.certificateNumber}
              </p>
            )}
            <p>
              Awarded to <b>{certificate.awardedToName}</b> for successfully
              completing the <b>{awardText}</b>.
            </p>
            {certificate.awardedAt && (
              <p>
                Awarded on{' '}
                <b>
                  {dayjs(parseUtcDate(certificate.awardedAt)).format(
                    'MM/DD/YYYY'
                  )}
                </b>
                {'.'}
              </p>
            )}
            {certificate.additionalContent && (
              <p>{certificate.additionalContent}</p>
            )}
            {certificate.certifyingTrainerName && (
              <p>
                Completed under certifying trainer{' '}
                {certificate.certifyingTrainerName},{' '}
                {certificate.certifyingTrainerTitle}.
              </p>
            )}
            {certificate.instructorName && (
              <p>Instructor: {certificate.instructorName}</p>
            )}
            <p>Thank you for your participation. {renewalText}</p>
          </Box>
        </>
      )}
    </Box>
  );
};
